import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { View } from '@/domain/mitarbeiter/features/rotationsplan/entities/views';
import { DesktopKalender } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop';
import { KalenderViewOptions } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/KalenderViewOptions';
import { YearNavigationHeader } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/desktop/components/YearNavigationHeader';
import { Grid } from '@/domain/mitarbeiter/features/rotationsplan/features/Plaene/Grid';
import { useRotationsplanFeatureContext } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/RotationsplanFeatureContext';
import {
  faFilter,
  faLayerGroup,
  faPlus,
  faUserGroup,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

/* eslint-disable max-lines-per-function */
export function DesktopPage({ view, year }: { view: string; year: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { store } = useRotationsplanFeatureContext();

  return (
    <div className="flex flex-col gap-8">
      <Tabs
        value={view}
        onValueChange={(value) => {
          navigate(`/mitarbeiter/rotationsplan/${year}/${value}`);
        }}>
        <div className="flex w-full justify-between">
          <TabsList className="bg-neutrals-300">
            <TabsTrigger className="w-full" value="planung">
              {t('domain.mitarbeiter.rotationsplan.tabs.planung')}
            </TabsTrigger>
            <TabsTrigger className="w-full" value="rotationsplaene">
              {t('domain.mitarbeiter.rotationsplan.tabs.rotationsplaene')}
            </TabsTrigger>
          </TabsList>
          {view === 'rotationsplaene' && (
            <div className="flex items-center gap-2">
              <Button className="flex gap-1 items-center" variant="ghost">
                <FontAwesomeIcon icon={faFilter} />
                {t('domain.mitarbeiter.rotationsplan.filter.label')}
              </Button>
              <Button size="icon" variant="default">
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            </div>
          )}
        </div>

        <TabsContent className="mt-8" value="planung">
          <Tabs defaultValue="mitarbeiter_rotationsplanKalenderByArbeitsbereiche">
            <div className="flex flex-col gap-4 w-full min-h-125 bg-white rounded-md items-start justify-start p-4">
              <div className="w-full flex items-start justify-between">
                <div className="flex flex-col gap-2">
                  <YearNavigationHeader jahr={year} view={view as View} />
                  <KalenderViewOptions />
                </div>
                <TabsList>
                  <Tooltip>
                    <TooltipTrigger>
                      <TabsTrigger
                        onClick={() =>
                          store.setKalenderView('byArbeitsbereiche')
                        }
                        value="mitarbeiter_rotationsplanKalenderByArbeitsbereiche">
                        <FontAwesomeIcon fixedWidth icon={faLayerGroup} />
                      </TabsTrigger>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t(
                        'domain.mitarbeiter.rotationsplan.tabs.anzeigeNachArbeitsbereichenTooltip',
                      )}
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger>
                      <TabsTrigger
                        onClick={() => store.setKalenderView('byMitarbeiter')}
                        value="mitarbeiter_rotationsplanKalenderByMitarbeiter">
                        <FontAwesomeIcon fixedWidth icon={faUserGroup} />
                      </TabsTrigger>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t(
                        'domain.mitarbeiter.rotationsplan.tabs.anzeigeNachMitarbeitendenTooltip',
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TabsList>
              </div>
              <TabsContent
                asChild
                value="mitarbeiter_rotationsplanKalenderByArbeitsbereiche">
                <DesktopKalender
                  view="arbeitsbereiche"
                  key={year}
                  jahr={year}
                />
              </TabsContent>
              <TabsContent
                asChild
                value="mitarbeiter_rotationsplanKalenderByMitarbeiter">
                <DesktopKalender view="mitarbeiter" key={year} jahr={year} />
              </TabsContent>
            </div>
          </Tabs>
        </TabsContent>
        <TabsContent className="mt-8" value="rotationsplaene">
          <Suspense fallback={<>Loading</>}>
            <Grid />
          </Suspense>
        </TabsContent>
      </Tabs>
    </div>
  );
}
